'use client';

import { AlertTriangle } from 'lucide-react';
import { PaymentType } from '@/types';
import { getCommissionPaymentTypeLabel } from '@/utils/payment-utils';
import { Alert, AlertDescription } from '@/components/ui/alert';

export interface FeeBreakdownProps {
  isDesktop: boolean;
  paymentType: PaymentType;
  organizerIncome: {
    [key in PaymentType]?: number;
  };
  totalPrice: {
    [key in PaymentType]?: number;
  };
  mercadoPagoFee: {
    days: number;
    commission: number;
  };
  fixedFeeAmount: number | null;
  hasPriceError: boolean;
  isModal?: boolean;
}

export function FeeBreakdown({
  isDesktop,
  paymentType,
  organizerIncome,
  totalPrice,
  mercadoPagoFee,
  fixedFeeAmount,
  hasPriceError,
  isModal = false,
}: FeeBreakdownProps) {
  function formatCurrency(value: number): string {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const renderPaymentType = (method: PaymentType) => {
    const tournamentPrice = totalPrice[method] || 0;

    // Calculate organizer income based on the tournament price and fees
    let calculatedIncome = 0;
    if (method === PaymentType.ALL || method === PaymentType.CASH) {
      calculatedIncome = tournamentPrice - (fixedFeeAmount || 0);
    } else if (method === PaymentType.MERCADOPAGO) {
      const mpFeePercentage = mercadoPagoFee.commission / 100;
      const mpFeeWithVAT = mpFeePercentage * 1.21;
      const mpFeeAmount = tournamentPrice * mpFeeWithVAT;
      calculatedIncome = tournamentPrice - mpFeeAmount - (fixedFeeAmount || 0);
    }

    const income = organizerIncome[method] || calculatedIncome;

    let feeDetails = null;

    if (method === PaymentType.ALL || method === PaymentType.CASH) {
      feeDetails = (
        <div className="flex justify-between">
          <span>Comisión GetMatch:</span>
          <span className="font-medium">
            -${formatCurrency(fixedFeeAmount || 0)}
          </span>
        </div>
      );
    } else if (method === PaymentType.MERCADOPAGO) {
      const mpFeePercentage = mercadoPagoFee.commission / 100;
      const mpFeeWithVAT = mpFeePercentage * 1.21;
      const mpFeeAmount = tournamentPrice * mpFeeWithVAT;

      feeDetails = (
        <>
          <div className="flex justify-between">
            <span>
              Comisión Mercado Pago ({mercadoPagoFee.commission}% + IVA):
            </span>
            <span className="font-medium">-${formatCurrency(mpFeeAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span>Comisión GetMatch:</span>
            <span className="font-medium">
              -${formatCurrency(fixedFeeAmount || 0)}
            </span>
          </div>
        </>
      );
    }

    if (isModal) {
      return (
        <div key={method} className="space-y-3">
          <h4 className="text-lg font-semibold text-gray-800">
            {getCommissionPaymentTypeLabel(method)}:
          </h4>
          <div
            className={`space-y-3 rounded-lg border p-4 ${hasPriceError ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
          >
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Precio del torneo:</span>
              <span className="text-sm font-medium">
                ${formatCurrency(tournamentPrice)}
              </span>
            </div>

            {feeDetails}

            <div
              className={`flex justify-between border-t pt-3 font-semibold ${hasPriceError ? 'border-orange-200' : 'border-gray-300'}`}
            >
              <span className="text-gray-900">Ingreso del organizador:</span>
              <span
                className={`text-lg ${hasPriceError ? 'text-orange-600' : 'text-gray-900'}`}
              >
                ${formatCurrency(income)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <li key={method} className="mb-4">
        <div className="font-semibold text-gray-800">
          {getCommissionPaymentTypeLabel(method)}:
        </div>
        <div
          className={`mt-2 space-y-1 rounded-md p-3 ${hasPriceError ? 'border border-orange-200 bg-orange-50' : 'bg-gray-50'}`}
        >
          <div className="flex justify-between">
            <span>Precio del torneo:</span>
            <span className="font-medium">
              ${formatCurrency(tournamentPrice)}
            </span>
          </div>

          {feeDetails}

          <div
            className={`flex justify-between border-t pt-1 font-semibold ${hasPriceError ? 'border-orange-200' : 'border-gray-200'}`}
          >
            <span>Ingreso del organizador:</span>
            <span className={hasPriceError ? 'text-orange-600' : ''}>
              ${formatCurrency(income)}
            </span>
          </div>
        </div>
      </li>
    );
  };

  const containerClass = isModal
    ? 'w-full space-y-6'
    : isDesktop
      ? 'w-1/2 rounded-lg border border-gray-200 p-4'
      : 'space-y-4 rounded-md bg-gray-100 p-4';

  if (isModal) {
    return (
      <div className={containerClass}>
        <h3 className="mb-6 text-lg font-semibold">
          Desglose por método de pago:
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {renderPaymentType(PaymentType.ALL)}
          {renderPaymentType(PaymentType.MERCADOPAGO)}
        </div>

        <Alert variant="warning" className="mt-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            En caso de cancelaciones o no presentación de participantes 48 horas
            antes del evento, la comisión por pagos en efectivo ya cobrada no
            será reintegrada a tu cuenta corriente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <h3 className={`${isDesktop ? 'mb-4' : ''} font-semibold`}>
        Desglose por método de pago:
      </h3>
      <ul>
        {paymentType === PaymentType.ALL ? (
          <>
            {renderPaymentType(PaymentType.ALL)}
            {renderPaymentType(PaymentType.MERCADOPAGO)}
          </>
        ) : (
          renderPaymentType(paymentType)
        )}
      </ul>

      {(paymentType === PaymentType.ALL || paymentType === PaymentType.CASH) && (
        <>
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              En caso de cancelaciones o no presentación de participantes 48
              horas antes del evento, la comisión por pagos en efectivo ya
              cobrada no será reintegrada a tu cuenta corriente.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}
